import React, { Dispatch, SetStateAction } from "react"
import { useAppDispatch } from "lib/store"
import { hideEditSidebar } from "lib/store/services/editSidebar/slice"

import { DELETE_EXTERNAL_PRICE_CONFIG } from "graphql/mutations/config/DeleteExternalPriceConfig"

import EditSidebar from "components/Ui/EditSidebar/EditSidebar"
import EditSidebarHeader from "components/Ui/EditSidebar/EditSidebarHeader"
import DeleteButton from "components/Ui/EditSidebar/DeleteButton"
import { Tab, TabList, TabPanel, Tabs } from "react-tabs"
import ConnectedStoreMarket from "components/Config/ConnectedStoreMarkets/ConnectedStoreMarket"
import { ReactComponent as ExternalPriceIcon } from "images/icons/circle-dollar.svg"
import { PROVIDER_CATEGORY, PROVIDER_TYPE } from "lib/types/common"
import { useMutation, useQuery } from "@apollo/client"
import { GET_EXTERNAL_PRICE_CONFIG } from "../../../../graphql/queries/config/GetExternalPriceProvider"
import ExternalPriceConfigInput from "../../../../components/Config/ExternalPrice/ExternalPriceConfigInput"
import { SubmitHandler, useForm } from "react-hook-form"
import { CREATE_OR_UPDATE_EXTERNAL_PRICE_CONFIG } from "../../../../graphql/mutations/config/CreateOrUpdateExternalPriceConfig"
import alertActions from "lib/store/services/Alert/AlertSlice"
import { InputExternalPriceConfig } from "lib/types/generated/graphql-types"
import PrimaryButton from "../../../../components/Ui/Buttons/PrimaryButton"
import { WithAuth } from "../../../../components/Auth"
import { PermissionGroups } from "../../../../routes/constants"

interface ExtendedInputExternalPriceConfig extends InputExternalPriceConfig {
  id: string
}

type Props = {
  refetch: () => void
  externalPriceConfigId: string
  setExternalPriceConfigId: Dispatch<SetStateAction<string>>
}

export const EditExternalPriceConfig = ({ refetch, externalPriceConfigId, setExternalPriceConfigId }: Props) => {
  const dispatch = useAppDispatch()
  const closeSidebar = () => {
    setExternalPriceConfigId("")
    dispatch(hideEditSidebar())
  }

  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<ExtendedInputExternalPriceConfig>({ mode: "onBlur" })

  const [addExternalPriceConfig, { loading }] = useMutation(CREATE_OR_UPDATE_EXTERNAL_PRICE_CONFIG, {
    onCompleted: () => {
      dispatch(
        alertActions.actions.createAlert({
          message: "Custom external price provider configuration successfully created",
          type: "success"
        })
      )
      refetch()
    },
    onError: () => {
      dispatch(
        alertActions.actions.createAlert({
          type: "error"
        })
      )
    }
  })

  const onSubmit: SubmitHandler<ExtendedInputExternalPriceConfig> = (data) => {
    addExternalPriceConfig({ variables: { ...data, id: externalPriceConfigId } })
  }


  const { data } = useQuery(GET_EXTERNAL_PRICE_CONFIG, { variables: { id: externalPriceConfigId } })

  const externalPriceConfig = data?.getExternalPriceConfig

  if (!externalPriceConfig) return null

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
    <EditSidebar cancelEvent={() => closeSidebar()}>
      <EditSidebarHeader
        icon={<ExternalPriceIcon />}
        title={externalPriceConfig.id}
        id={externalPriceConfig.id}
        cancelEvent={() => closeSidebar()}
      >
        <WithAuth requiredGroups={PermissionGroups.providers.write}>
          <DeleteButton
            id={externalPriceConfigId}
            name={externalPriceConfigId}
            entity={"Custom external price provider configuration"}
            redirectUrl="/settings/providers/external-price"
            mutation={DELETE_EXTERNAL_PRICE_CONFIG}
            refetch={refetch}
            postDeleteFunction={() => setExternalPriceConfigId("")}
            secureDelete={true}
          />
          <PrimaryButton type="submit" loading={loading}>
            Save
          </PrimaryButton>
        </WithAuth>

      </EditSidebarHeader>

      <Tabs>
        <TabList>
          <Tab>General</Tab>
          <Tab>Connected store markets</Tab>
        </TabList>
        <TabPanel>
          <ExternalPriceConfigInput control={control} errors={errors} defaultValues={externalPriceConfig} />
        </TabPanel>
        <TabPanel>
          <ConnectedStoreMarket
            providerId={externalPriceConfigId}
            providerType={PROVIDER_TYPE.EXTERNAL_PRICE}
            providerCategory={PROVIDER_CATEGORY.CART_CAPABILITY}
          />
        </TabPanel>
      </Tabs>
    </EditSidebar>
    </form>
  )
}
